import { COLORS, type ColorVariant, type SmallIconName } from '@alto/design-library-tokens';
import React from 'react';
import { Pressable } from 'react-native';
import { Row } from '../../../containers';
import { Icon } from '../../../icon';
import { getTextColorFromType } from '../../../icon/src/getTextColorFromType';
import { AltoSpinningLoader } from '../../../loaders';
import { XsSpacing } from '../../../separators';
import { Description } from '../../../typography';

type Position = 'left' | 'right';

export type ListDescriptionProps = {
  readonly children?: string | React.ReactElement<typeof Description>;
  readonly iconName?: SmallIconName;
  readonly loading?: boolean;
  /**
   * Horizontal alignment of the loading spinner. Only applies when `loading` is true.
   * Defaults to `'center'` to preserve existing behavior. Pass `'start'` when the loading
   * state replaces a row that has a left-anchored icon, so the spinner sits where the
   * icon will appear once loading completes (e.g., price loading in checkout med cards).
   */
  readonly loadingAlign?: 'center' | 'start';
  readonly onPress?: () => void;
  readonly position?: Position;
  readonly semibold?: boolean;
  readonly type?: ColorVariant;
  readonly fullWidth?: boolean;
};

export const ListDescription = ({
  children,
  iconName,
  loading,
  loadingAlign = 'center',
  onPress,
  position = 'left',
  semibold,
  type = 'grey',
  fullWidth = false,
}: ListDescriptionProps) => {
  // `secondary` is how callers spell "this row is a link" — every call site pairs it with
  // `onPress` and a leading icon. Render both in the link color so the icon matches its text.
  // Requiring `onPress` keeps a future non-pressable `secondary` row out of the link treatment
  // rather than trusting the convention to hold.
  const isLink = type === 'secondary' && !!onPress;
  const iconType = isLink ? 'link' : type;
  const textColor = isLink ? COLORS.TEXT_COLORS.LINK : getTextColorFromType(type);
  const Spinner = (
    <AltoSpinningLoader
      accessibilityLabel="Loading"
      small
    />
  );
  const LoadingContent =
    loadingAlign === 'start' ? <Row flexShrink={1}>{Spinner}</Row> : <Row centerHorizontally>{Spinner}</Row>;
  const Content = loading ? (
    LoadingContent
  ) : (
    <Row
      flexShrink={1}
      wrap={false}
    >
      {!!iconName && position === 'left' && (
        <>
          <Icon
            name={iconName}
            type={iconType}
          />
          <XsSpacing />
        </>
      )}
      <Row
        centerVertically
        flexShrink={fullWidth ? 0 : 1}
        wrap
      >
        <Description
          fontFamily={semibold ? 'semibold' : undefined}
          color={textColor}
        >
          {children}
        </Description>
      </Row>
      {!!iconName && position === 'right' && (
        <>
          <XsSpacing />
          <Icon
            name={iconName}
            type={iconType}
          />
        </>
      )}
    </Row>
  );

  return onPress ? (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
    >
      {Content}
    </Pressable>
  ) : (
    Content
  );
};
